import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  booleanAttribute,
} from '@angular/core';
import {
  DsTagPillComponent,
  type DsTagPillVariant,
} from '../tags/ds-tag-pill.component';

export type DsCardImageBackgroundTagPlacement = 'top' | 'above-text';

@Component({
  selector: 'ds-card-image-background',
  standalone: true,
  imports: [NgTemplateOutlet, DsTagPillComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (href) {
      <a
        [class]="hostClasses"
        [style.--ds-card-image-background-resolved-text-color]="resolvedTextColor"
        [style.--ds-card-image-background-resolved-gradient-color]="resolvedGradientColor"
        [attr.href]="disabled ? null : href"
        [attr.target]="target || null"
        [attr.rel]="rel || null"
        [attr.tabindex]="disabled ? -1 : null"
        [attr.aria-disabled]="disabled ? 'true' : null"
        [attr.aria-label]="ariaLabel || null"
        (click)="handleActivation($event)"
      >
        <ng-container [ngTemplateOutlet]="cardContent" />
      </a>
    } @else {
      <button
        type="button"
        [class]="hostClasses"
        [style.--ds-card-image-background-resolved-text-color]="resolvedTextColor"
        [style.--ds-card-image-background-resolved-gradient-color]="resolvedGradientColor"
        [disabled]="disabled"
        [attr.aria-label]="ariaLabel || null"
        (click)="handleActivation($event)"
      >
        <ng-container [ngTemplateOutlet]="cardContent" />
      </button>
    }

    <ng-template #cardContent>
      <img class="ds-card-image-background__image" [src]="imageSrc" alt="" />
      @if (tagLabel && tagPlacement === 'top') {
        <span class="ds-card-image-background__top">
          <ds-tag-pill
            class="ds-card-image-background__tag ds-card-image-background__tag--top"
            [label]="tagLabel"
            [variant]="tagVariant"
            size="xs"
            [invert]="tagInvert"
          />
        </span>
      }
      <span class="ds-card-image-background__content">
        <span class="ds-card-image-background__copy">
          @if (tagLabel && tagPlacement === 'above-text') {
            <ds-tag-pill
              class="ds-card-image-background__tag"
              [label]="tagLabel"
              [variant]="tagVariant"
              size="xs"
              [invert]="tagInvert"
            />
          }
          <span class="ds-card-image-background__text-wrap">
            <span class="ds-card-image-background__gradient" aria-hidden="true"></span>
            <span class="ds-card-image-background__text">{{ text }}</span>
          </span>
        </span>
      </span>
    </ng-template>
  `,
  styles: [`
    :host {
      display: block;
      min-width: 0;
    }

    .ds-card-image-background {
      appearance: none;
      position: relative;
      display: flex;
      flex-direction: column;
      width: 100%;
      min-width: 0;
      margin: 0;
      padding: 0;
      overflow: hidden;
      border: 0;
      border-radius: var(--ds-card-image-background-border-radius);
      background: var(--ds-card-image-background-resolved-gradient-color);
      box-shadow: var(--ds-card-image-background-shadow);
      box-sizing: border-box;
      color: var(--ds-card-image-background-resolved-text-color);
      font-family: var(--ds-foundation-font-family-primary, 'Sura Sans', system-ui, sans-serif);
      text-align: left;
      text-decoration: none;
      transform: scale(1);
      transform-origin: center;
      cursor: pointer;
      outline: none;
      transition:
        transform var(--ds-card-image-background-transition-duration) ease,
        opacity var(--ds-card-image-background-transition-duration) ease,
        box-shadow var(--ds-card-image-background-transition-duration) ease;
    }

    .ds-card-image-background::after {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 2;
      border-radius: inherit;
      background: transparent;
      pointer-events: none;
      transition: background-color var(--ds-card-image-background-transition-duration) ease;
    }

    .ds-card-image-background:hover:not(.ds-card-image-background--disabled),
    .ds-card-image-background:active:not(.ds-card-image-background--disabled) {
      transform: scale(var(--ds-card-image-background-interaction-scale));
    }

    .ds-card-image-background:hover:not(.ds-card-image-background--disabled)::after {
      background: var(--ds-card-image-background-hover-overlay);
    }

    .ds-card-image-background:active:not(.ds-card-image-background--disabled)::after {
      background: var(--ds-card-image-background-pressed-overlay);
    }

    .ds-card-image-background:focus-visible:not(.ds-card-image-background--disabled) {
      outline: var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: var(--ds-focus-gap);
    }

    .ds-card-image-background--disabled {
      opacity: calc(var(--ds-card-image-background-disabled-opacity) / 100px);
      cursor: not-allowed;
    }

    .ds-card-image-background__image {
      position: absolute;
      inset: 0;
      z-index: 0;
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
      pointer-events: none;
    }

    .ds-card-image-background__top {
      position: relative;
      z-index: 3;
      display: flex;
      padding: var(--ds-card-image-background-padding) var(--ds-card-image-background-padding) 0;
      pointer-events: none;
    }

    .ds-card-image-background__content {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      margin-top: auto;
      padding: var(--ds-card-image-background-content-top-spacing)
        var(--ds-card-image-background-padding)
        var(--ds-card-image-background-padding);
      box-sizing: border-box;
      pointer-events: none;
    }

    /* Solo protege al texto: nace contentTopSpacing sobre el texto, desplazado gradientOffset hacia
       abajo, y se extiende hasta los bordes inferior y laterales de la card. El tag above-text
       queda encima (z-index 3). */
    .ds-card-image-background__gradient {
      position: absolute;
      top: calc(
        var(--ds-card-image-background-gradient-offset) -
          var(--ds-card-image-background-content-top-spacing)
      );
      right: calc(-1 * var(--ds-card-image-background-padding));
      bottom: calc(-1 * var(--ds-card-image-background-padding));
      left: calc(-1 * var(--ds-card-image-background-padding));
      z-index: 1;
      display: block;
      background: linear-gradient(
        to bottom,
        transparent 0,
        var(--ds-card-image-background-resolved-gradient-color)
          var(--ds-card-image-background-content-top-spacing)
      );
      opacity: calc(var(--ds-card-image-background-gradient-opacity) / 100px);
      mix-blend-mode: multiply;
      pointer-events: none;
    }

    .ds-card-image-background__copy {
      display: flex;
      width: 100%;
      min-width: 0;
      flex-direction: column;
      align-items: flex-start;
      gap: var(--ds-card-image-background-tag-text-gap);
    }

    .ds-card-image-background__tag {
      position: relative;
      z-index: 3;
      display: block;
      max-width: 100%;
    }

    .ds-card-image-background__text-wrap {
      position: relative;
      display: block;
      width: 100%;
    }

    .ds-card-image-background__text {
      position: relative;
      z-index: 3;
      display: -webkit-box;
      width: 100%;
      overflow: hidden;
      color: var(--ds-card-image-background-resolved-text-color);
      font-size: var(--ds-card-image-background-text-font-size);
      font-weight: var(--ds-card-image-background-text-font-weight);
      line-height: var(--ds-card-image-background-text-line-height);
      overflow-wrap: anywhere;
      white-space: pre-line;
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    }

    .ds-card-image-background--no-truncate .ds-card-image-background__text {
      display: block;
      text-overflow: clip;
      -webkit-line-clamp: unset;
    }
  `],
})
export class DsCardImageBackgroundComponent {
  @Input({ required: true }) text = '';
  @Input({ required: true }) imageSrc = '';
  @Input() tagLabel = '';
  @Input() tagPlacement: DsCardImageBackgroundTagPlacement = 'above-text';
  @Input() tagVariant: DsTagPillVariant = 'neutral';
  @Input({ transform: booleanAttribute }) tagInvert = false;
  @Input({ transform: booleanAttribute }) disabled = false;
  @Input({ transform: booleanAttribute }) truncateText = true;
  @Input() href = '';
  @Input() target = '';
  @Input() rel = '';
  @Input() ariaLabel = '';
  @Input() textColor: string | null = null;
  @Input() gradientColor: string | null = null;

  @Output() clicked = new EventEmitter<MouseEvent>();

  get resolvedTextColor(): string {
    return this.textColor ?? 'var(--ds-card-image-background-text-color)';
  }

  get resolvedGradientColor(): string {
    return this.gradientColor ?? 'var(--ds-card-image-background-gradient-color)';
  }

  get hostClasses(): string {
    return [
      'ds-card-image-background',
      `ds-card-image-background--tag-${this.tagPlacement}`,
      this.disabled ? 'ds-card-image-background--disabled' : '',
      this.truncateText ? '' : 'ds-card-image-background--no-truncate',
    ].filter(Boolean).join(' ');
  }

  handleActivation(event: MouseEvent): void {
    if (this.disabled) {
      event.preventDefault();
      return;
    }
    this.clicked.emit(event);
  }
}
